import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Date/Hour Cell Renderer Component
 *
 * Reusable cell renderer for displaying date and time values.
 * Formats as "DD-MM-YYYY HH:mm:ss" matching the FormatOtherDateHourPipe behavior.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'date',
 *   cellRenderer: DateHourCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-date-hour-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-content">
      {{ formattedDateTime }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateHourCellRendererComponent implements CellRenderer<any, string | Date> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, string | Date>;

  /**
   * Get formatted date and time (DD-MM-YYYY HH:mm:ss)
   * Matches the behavior of FormatOtherDateHourPipe with includeTime=true
   */
  get formattedDateTime(): string {
    const date = this.params?.value;

    if (date) {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const seconds = d.getSeconds().toString().padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    }

    return '--:--:--';
  }
}
